import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Tooltip,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { RemoveCircleOutline, ContentCopy, Check } from '@mui/icons-material';
import ParameterList from './ParameterEditor/ParameterList';
import ParameterValueEditor from './ParameterEditor/ParameterValueEditor';
import ConfirmationDialog from './ConfirmationDialog';

const SectionHeader = ({ title, action }) => (
    <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
    >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
        {action}
    </Stack>
);

const Section = ({ title, action, children, sx, bodySx }) => (
    <Paper
        variant="outlined"
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, ...sx }}
    >
        <SectionHeader title={title} action={action} />
        <Box sx={{ p: 1.25, flex: 1, minHeight: 0, overflow: 'auto', ...bodySx }}>{children}</Box>
    </Paper>
);

const ParameterEditor = ({ selectedAppIndex, appIndex, selectedMavId, paramListTimestamp }) => {
    const [selectedParam, setSelectedParam] = useState(null);
    const [paramValue, setParamValue] = useState(0);
    const [openGroups, setOpenGroups] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [params, setParams] = useState({});
    const [changedParams, setChangedParams] = useState({});
    const [displayParams, setDisplayParams] = useState({});
    const [openDiscardDialog, setOpenDiscardDialog] = useState(false);
    const [openRebootDialog, setOpenRebootDialog] = useState(false);
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [openWriteDialog, setOpenWriteDialog] = useState(false);
    const [openBlockingDialog, setOpenBlockingDialog] = useState(false);

    useEffect(() => {
        if (!window.mav[selectedMavId]) return;
        if (window.paramList[selectedMavId] && Object.keys(window.paramList[selectedMavId]).length > 0) {
            setParams({ ...window.paramList[selectedMavId] });
            setOpenBlockingDialog(false);
        }
    }, [paramListTimestamp]);

    useEffect(() => {
        if (window.paramList[selectedMavId] && Object.keys(window.paramList[selectedMavId]).length > 0) {
            setParams({ ...window.paramList[selectedMavId] });
            setChangedParams({});
            setSelectedParam(null);
        } else {
            window.api.requestParamList(selectedMavId);
            if (selectedAppIndex === appIndex) {
                setOpenBlockingDialog(true);
            }
        }
    }, [selectedMavId]);

    useEffect(() => {
        const sortedParams = {};
        if (selectedMavId && window.paramList[selectedMavId]) {
            const query = searchQuery.toLowerCase();
            Object.keys(params).sort().forEach((paramName) => {
                let paramGroup = paramName.split('_')[0];
                if (!paramGroup) return;
                paramGroup = paramGroup.replace(/\d+/g, '');
                if (query && !paramName.toLowerCase().includes(query)) return;
                if (sortedParams[paramGroup] === undefined) {
                    sortedParams[paramGroup] = {};
                }
                sortedParams[paramGroup][paramName] = params[paramName];
            });
            setDisplayParams(sortedParams);
        }
    }, [params, searchQuery]);

    const handleRefreshClick = async () => {
        window.api.requestParamList(selectedMavId);
    };

    const handleRowClick = (paramName) => {
        setSelectedParam(paramName);
        if (changedParams[paramName]) {
            setParamValue(changedParams[paramName].newValue);
        } else {
            setParamValue(params[paramName].value);
        }
    };

    const handleParamValueChange = (event) => {
        const value = Number(event.target.value);
        setParamValue(value);
        if (!selectedParam || !params[selectedParam]) return;
        const oldValue = params[selectedParam].value;
        setChangedParams((prev) => {
            const next = { ...prev };
            if (value === oldValue) {
                delete next[selectedParam];
            } else {
                next[selectedParam] = { oldValue, newValue: value };
            }
            return next;
        });
    };

    const handleTrackedParamValueChange = (value) => {
        setParamValue(value);
        if (!selectedParam || !params[selectedParam]) return;
        const oldValue = params[selectedParam].value;
        setChangedParams((prev) => {
            const next = { ...prev };
            if (value === oldValue) {
                delete next[selectedParam];
            } else {
                next[selectedParam] = { oldValue, newValue: value };
            }
            return next;
        });
    };

    const handleGroupClick = (group) => {
        setOpenGroups((prev) => ({ ...prev, [group]: !prev[group] }));
    };

    const handleRemoveParam = (param) => {
        setChangedParams((prev) => {
            const next = { ...prev };
            delete next[param];
            return next;
        });
    };

    const handleCloseBlocking = () => {
        setSelectedParam(null);
        setOpenBlockingDialog(false);
    };

    const confirmReboot = () => {
        window.api.reboot(selectedMavId);
        setOpenRebootDialog(false);
    };

    const confirmResetToDefault = () => {
        window.api.resetParamsToDefault(selectedMavId);
        setChangedParams({});
        setOpenResetDialog(false);
    };

    const confirmDiscardChanges = () => {
        setChangedParams({});
        setOpenDiscardDialog(false);
    };

    const confirmWriteParams = () => {
        const paramsToWrite = {};
        Object.keys(changedParams).forEach((param) => {
            paramsToWrite[param] = {
                value: changedParams[param].newValue,
                type: window.paramList[selectedMavId][param].type,
            };
        });
        window.api.setParams(selectedMavId, paramsToWrite);
        setOpenWriteDialog(false);
        setChangedParams({});
    };

    const handleOnParamLoaded = (event) => {
        const { mavId, loadedParams } = event.detail;
        const diffParams = {};
        Object.keys(loadedParams).forEach((param) => {
            if (loadedParams[param] !== window.paramList[mavId][param].value) {
                diffParams[param] = {
                    oldValue: window.paramList[mavId][param].value,
                    newValue: loadedParams[param],
                };
            }
        });
        setChangedParams(diffParams);
    };

    useEffect(() => {
        window.addEventListener('param-loaded', handleOnParamLoaded);
        return () => window.removeEventListener('param-loaded', handleOnParamLoaded);
    }, []);

    const renderParamNameInList = (paramName) => {
        if (window.paramList[selectedMavId] && window.paramList[selectedMavId][paramName]) {
            const param = window.paramList[selectedMavId][paramName];
            if (param?.metaData?.fields?.RebootRequired) return `${paramName}*`;
            return paramName;
        }
        return '';
    };

    const changedCount = Object.keys(changedParams).length;
    const [copiedParam, setCopiedParam] = useState(false);
    const handleCopyParamName = () => {
        if (!selectedParam) return;
        try {
            navigator.clipboard.writeText(selectedParam);
            setCopiedParam(true);
            setTimeout(() => setCopiedParam(false), 1200);
        } catch (e) { /* ignore */ }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <Box sx={{ flex: 1, minHeight: 0 }} style={{ display: 'flex', flexDirection: 'column', margin: 2 }}>
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'row',
                        minHeight: 0,
                        gap: '2px',
                        p: '2px',
                        boxSizing: 'border-box',
                    }}
                >
            {/* Left: editor + changes */}
            <Box sx={{ flex: 3, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                <Section
                    title={
                        selectedParam ? (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {`Edit Parameter — ${selectedParam}`}
                                </Typography>
                                <Tooltip title={copiedParam ? 'Copied!' : 'Copy parameter name'}>
                                    <IconButton size="small" onClick={handleCopyParamName}>
                                        {copiedParam
                                            ? <Check fontSize="inherit" color="success" />
                                            : <ContentCopy fontSize="inherit" />}
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        ) : 'Select a parameter to edit'
                    }
                    sx={{ flex: 1, minHeight: 0 }}
                >
                    <ParameterValueEditor
                        selectedMavId={selectedMavId}
                        selectedParam={selectedParam}
                        params={params}
                        paramValue={paramValue}
                        setParamValue={handleTrackedParamValueChange}
                        handleParamValueChange={handleParamValueChange}
                    />
                </Section>

                <Section
                    title={`Pending Changes${changedCount ? ` (${changedCount})` : ''} — * Reboot required`}
                    action={
                        <Stack direction="row" spacing={0.75}>
                            <Tooltip title="Discard all pending changes">
                                <span>
                                    <Button
                                        color="warning"
                                        variant="outlined"
                                        disabled={!changedCount}
                                        onClick={() => setOpenDiscardDialog(true)}
                                    >
                                        Discard
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Reboot vehicle">
                                <Button color="warning" variant="outlined" onClick={() => setOpenRebootDialog(true)}>
                                    Reboot
                                </Button>
                            </Tooltip>
                            <Tooltip title="Reset all parameters to default">
                                <Button color="error" variant="outlined" onClick={() => setOpenResetDialog(true)}>
                                    Reset Default
                                </Button>
                            </Tooltip>
                            <Tooltip title="Write pending changes to vehicle">
                                <span>
                                    <Button
                                        variant="contained"
                                        disabled={!changedCount}
                                        onClick={() => setOpenWriteDialog(true)}
                                    >
                                        Write
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                    }
                    sx={{ flex: 1, minHeight: 0 }}
                    bodySx={{ p: 0 }}
                >
                    {changedCount === 0 ? (
                        <Box
                            sx={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'text.disabled',
                            }}
                        >
                            <Typography variant="caption">No pending changes</Typography>
                        </Box>
                    ) : (
                        <TableContainer sx={{ maxHeight: '100%' }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell align="right">Origin</TableCell>
                                        <TableCell align="right">New</TableCell>
                                        <TableCell align="right" sx={{ width: 48 }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.keys(changedParams).map((param) => (
                                        <TableRow key={param} hover>
                                            <TableCell
                                                onClick={() => handleRowClick(param)}
                                                sx={{ cursor: 'pointer', fontFamily: 'monospace' }}
                                            >
                                                {renderParamNameInList(param)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: 'error.main', fontFamily: 'monospace' }}>
                                                {changedParams[param].oldValue}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: 'success.main', fontFamily: 'monospace' }}>
                                                {changedParams[param].newValue}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton color="error" onClick={() => handleRemoveParam(param)}>
                                                    <RemoveCircleOutline fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Section>
            </Box>

            {/* Right: parameter list */}
            <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <ParameterList
                    selectedMavId={selectedMavId}
                    params={params}
                    displayParams={displayParams}
                    openGroups={openGroups}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    handleGroupClick={handleGroupClick}
                    handleRowClick={handleRowClick}
                    handleRefreshClick={handleRefreshClick}
                />
            </Box>

            <ConfirmationDialog
                open={openDiscardDialog}
                onClose={() => setOpenDiscardDialog(false)}
                onConfirm={confirmDiscardChanges}
                title="Discard Changes"
                description="Are you sure you want to discard all changes?"
            />
            <ConfirmationDialog
                open={openRebootDialog}
                onClose={() => setOpenRebootDialog(false)}
                onConfirm={confirmReboot}
                title="Reboot Vehicle"
                description="Are you sure to reboot the vehicle?"
            />
            <ConfirmationDialog
                open={openWriteDialog}
                onClose={() => setOpenWriteDialog(false)}
                onConfirm={confirmWriteParams}
                title="Write Parameters"
                description="Are you sure to write the changes to the vehicle?"
            />
            <ConfirmationDialog
                open={openResetDialog}
                onClose={() => setOpenResetDialog(false)}
                onConfirm={confirmResetToDefault}
                title="Reset To Default Parameters"
                description="Are you sure to reset all parameters to default?"
            />

            <Dialog open={openBlockingDialog} aria-labelledby="alert-dialog-title">
                <DialogTitle id="alert-dialog-title">Loading Parameters</DialogTitle>
                <DialogContent>
                    <DialogContentText>Please wait while the parameters are being loaded.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseBlocking} color="primary" autoFocus>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
                </Box>
            </Box>
        </div>
    );
};

export default ParameterEditor;
